import type { Category } from "../types";
import {
  QUERY_URL_PARAM,
  RESULT_TYPE_COURSE,
  RESULT_TYPE_DEGREE_PROGRAM,
  RESULT_TYPE_EXECUTIVE_EDUCATION,
  RESULT_TYPE_PROGRAM,
  RESULT_TYPE_URL_PARAM,
} from "@/components/Search/constants";

const CATEGORY_KEY_TO_TAB: Record<Category["key"], string> = {
  course: RESULT_TYPE_COURSE,
  executiveEducation: RESULT_TYPE_EXECUTIVE_EDUCATION,
  certificate: RESULT_TYPE_PROGRAM,
  masters: RESULT_TYPE_DEGREE_PROGRAM,
  bachelors: RESULT_TYPE_DEGREE_PROGRAM,
};

/**
 * Builds a `/search`-compatible query string (no leading `?`) for `rv_product_summary`,
 * using the same URL parameters as the search page (`SearchContent`, `useSearchInitialState`).
 *
 * **Contract (see `VALID_SEARCH_URL_PARAMS` and `FILTER_CATEGORIES` in Search constants):**
 * - `tab` — Line-of-business tab: `course` | `program` | `executive-education` | `degree-program` | …
 *   (maps to the `product` facet via `RESULT_PRODUCT_TYPE_MAP`, e.g. `course` → `"Course"`).
 * - `q` — Full-text search for subcategory rows (e.g. `"Featured"`, `"Business"`). Subcategory
 *   labels are marketing copy; they are not guaranteed to match Subject facet values, so we use
 *   `q` instead of `attributes` for keyword search.
 *
 * Examples: `?tab=course`, `?tab=course&q=Featured`, `?tab=course&attributes=Data` (facet-only
 * when you need an exact Subject facet from Algolia).
 */
export function buildDefaultSearchQuery(
  categoryKey: Category["key"],
  subcategoryLabel: string,
): string {
  const tab = CATEGORY_KEY_TO_TAB[categoryKey] ?? CATEGORY_KEY_TO_TAB.course;
  const label = subcategoryLabel?.trim() ?? "";

  if (!label || label.toLowerCase() === "popular") {
    return `${RESULT_TYPE_URL_PARAM}=${tab}`;
  }

  return `${RESULT_TYPE_URL_PARAM}=${tab}&${QUERY_URL_PARAM}=${encodeURIComponent(label)}`;
}
